<script>
import { Download, Upload } from '@lucide/vue';
import { exportData, importData, parseBackup } from './../storage.js';
import { plural } from '../config.js';

export default {
  data() {
    return {
      // A backup that was read and waits for the go-ahead to replace what's here
      pending: null,
      error: "",
      done: "",
    }
  },
  components: {
    Download, Upload,
  },
  props: {
    classCount: Number,
    roomCount: Number,
  },
  emits: ['dataImported'],
  computed: {
    hasData() {
      return this.classCount > 0 || this.roomCount > 0
    },
    pendingSummary() {
      return `${plural(this.pending.classes.length, 'Klasse', 'Klassen')} und ${plural(this.pending.rooms.length, 'Zimmer', 'Zimmer')}`
    },
    currentSummary() {
      return `${plural(this.classCount, 'Klasse', 'Klassen')} und ${plural(this.roomCount, 'Zimmer', 'Zimmer')}`
    },
  },
  methods: {
    readFile: function(event) {
        const dataFile = event.target.files[0]
        if (!dataFile) return
        this.error = ""
        this.done = ""
        this.pending = null
        const reader = new FileReader();
        reader.readAsText(dataFile, "UTF-8");
        reader.onload = (loaded) => {
            try {
              this.pending = parseBackup(loaded.target.result)
              // Nothing to lose: import straight away
              if (!this.hasData) this.confirmImport()
            } catch (error) {
              this.error = "Das ist keine Sitzplan-Sicherung. Wähle die Datei, die du hier exportiert hast (sie endet auf .json)."
            }
        }
        reader.onerror = () => {
            this.error = "Die Datei liess sich nicht lesen. Versuch es nochmals."
        }
        event.target.value = ""
    },
    confirmImport: function() {
        importData(this.pending)
        this.done = `Importiert: ${this.pendingSummary}.`
        this.pending = null
        this.$emit("dataImported")
    },
    exportData: function() {
        const blob = new Blob([JSON.stringify(exportData())], { type: 'application/json' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `sitzplan-sicherung-${new Date().toISOString().slice(0, 10)}.json`
        link.click()
        URL.revokeObjectURL(link.href)
        this.error = ""
        this.done = "Sicherung heruntergeladen."
    },
  },
}
</script>

<template>
    <div class="data-menu">
        <p class="hint">
            Deine Klassen, Zimmer und Sitzpläne sind in diesem Browser auf deinem Gerät gespeichert.
            Löschst du die Browserdaten oder wechselst du das Gerät, sind sie weg.
            Mit einer Sicherung nimmst du sie mit: exportiere eine Datei und importiere sie später wieder.
        </p>
        <div class="actions">
            <button type="button" class="btn btn-primary" :disabled="!hasData" v-on:click="exportData()">
                <Download aria-hidden="true" /> Exportieren
            </button>
            <button type="button" class="btn" v-on:click="$refs.file.click()">
                <Upload aria-hidden="true" /> Importieren
            </button>
            <input v-on:change="readFile" type="file" accept=".json,application/json" ref="file" class="visually-hidden" tabindex="-1" aria-hidden="true" />
        </div>
        <div v-if="pending" class="form-note confirm">
            <p>Die Sicherung enthält {{ pendingSummary }}. Sie ersetzt, was jetzt hier ist: {{ currentSummary }}.</p>
            <div class="actions">
                <button type="button" class="btn btn-sm" @click="confirmImport">Ersetzen</button>
                <button type="button" class="btn btn-quiet btn-sm" @click="pending = null">Abbrechen</button>
            </div>
        </div>
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <p v-else-if="done" class="form-note" role="status">{{ done }}</p>
    </div>
</template>

<style scoped>
.data-menu {
    display: grid;
    gap: var(--s-4);
}

.confirm {
    display: grid;
    gap: var(--s-3);
}
</style>
